import {
  ACESFilmicToneMapping,
  PCFShadowMap,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
} from 'three';
import { damp } from '../utils/rng';

/**
 * Enveloppe le WebGLRenderer et la camera : reglages colorimetriques, pixel
 * ratio, redimensionnement et champ de vision.
 * cf. doc/01-rendu-et-couleur.md
 */

/** Champ de vision en vue de conduite, en degres. */
export const FOV_DRIVE = 58;
/** Champ de vision dans la lunette : grossissement d'environ 2,6x. */
export const FOV_SIGHT = 22;

export class Renderer {
  readonly gl: WebGLRenderer;
  readonly camera: PerspectiveCamera;
  private targetFov = FOV_DRIVE;
  private readonly onResize = () => this.resize();

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.gl = new WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    // Au-dela de 2 le cout en pixels explose sans gain visible.
    this.gl.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.gl.toneMapping = ACESFilmicToneMapping;
    // Le shader de ciel sort des luminances physiques, tres au-dessus de 1 :
    // sans exposition basse, ACES ecrete tout en blanc.
    this.gl.toneMappingExposure = 0.46;
    this.gl.shadowMap.enabled = true;
    // PCFSoftShadowMap a ete retire en r18x ; PCFShadowMap est le reglage courant.
    this.gl.shadowMap.type = PCFShadowMap;

    // Le far plane doit englober le massif qui ferme le champ de tir.
    this.camera = new PerspectiveCamera(FOV_DRIVE, 1, 0.3, 5000);
    this.camera.position.set(0, 8, -20);

    this.resize();
    window.addEventListener('resize', this.onResize);
  }

  /**
   * Vise un champ de vision ; la transition est lissee dans `update`.
   *
   * Un passage instantane de 58 a 22 degres est desagreable et, surtout,
   * illisible : on perd le repere de ce qu'on visait.
   */
  setFov(fov: number): void {
    this.targetFov = fov;
  }

  update(dt: number): void {
    if (Math.abs(this.camera.fov - this.targetFov) < 0.01) return;
    this.camera.fov = damp(this.camera.fov, this.targetFov, 11, dt);
    this.camera.updateProjectionMatrix();
  }

  resize(): void {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.gl.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  render(scene: Scene): void {
    this.gl.render(scene, this.camera);
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    this.gl.dispose();
  }
}
